import { Box, Text } from '@factorialco/deprecated-design-system'
import { Spinner } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

import { formatPrice } from 'modules/billing/lib/f25Prices'
import useSelfUpsellPrices from 'modules/upsell/hooks/useSelfUpsellPrices'
import type { ApiDiscount } from 'modules/upsell/lib/selfUpsell/discountTypes'
import { DEFAULT_DISCOUNT_PERIOD } from 'modules/upsell/lib/selfUpsell/discountTypes'
import { getPeriodUnitTranslation } from 'modules/upsell/lib/selfUpsell/discountUtils'
import type Plan from 'modules/upsell/lib/selfUpsell/plans/Plan'
import { useSelfUpsellContext } from 'modules/upsell/store/SelfUpsellProvider'

type ItemInfoProps = {
  title: string
  description: string | React.ReactNode
  price: string
  originalPrice?: string
}

const ItemInfo = ({ title, description, price, originalPrice }: ItemInfoProps) => {
  return (
    <Box flexDirection="row" justifyContent="spaceBetween" gap="s8">
      <Box gap="s2">
        <Text.Normal size="200" color="grey1000">
          {title}
        </Text.Normal>
        {typeof description === 'string' ? (
          <Text.Normal size="100" color="grey700">
            {description}
          </Text.Normal>
        ) : (
          <Box gap="s2">{description}</Box>
        )}
      </Box>
      <Box flexDirection="row" gap="s4" alignItems="center">
        {originalPrice && (
          <Text.Normal size="200" color="grey700" stroked>
            {originalPrice}
          </Text.Normal>
        )}
        <Text.Normal size="200" color="grey1000">
          {price}
        </Text.Normal>
      </Box>
    </Box>
  )
}

type PaymentDetailProps = {
  addon: Plan
  crossSellingAddons?: Plan[] | undefined
  mandatoryCrossSellingAddons?: Plan[] | undefined
  billingDate: string
  discounts: ApiDiscount[] | undefined
  loadingPrice: boolean
}

const PaymentDetail = ({
  addon,
  crossSellingAddons,
  mandatoryCrossSellingAddons,
  billingDate,
  discounts,
  loadingPrice,
}: PaymentDetailProps) => {
  const {
    configPlan,
    seats,
    currentSeats,
    currency,
    isYearly,
    isExpenses,
    discount: manualDiscount,
    period,
    computeCrossSellingTotalPrice: _computeCrossSellingTotalPrice,
  } = useSelfUpsellContext()

  const {
    perSeatPrice,
    feePrice,
    stairstepPrice,
    tieredPrice,
    annualFeePrice,
    discountedAddons,
    formattedTotalPrice,
  } = useSelfUpsellPrices({
    addon,
    discounts,
    crossSellingAddons,
    mandatoryCrossSellingAddons,
  })

  const includedSeatsPerModule = tieredPrice?.tiers?.[0]?.endingUnit
  const yearlyText = isYearly()
    ? `x ${i18n.t('self_upsell.second_step.months', { months: 12 })}`
    : ''

  const descriptionText = isExpenses()
    ? [
        `${formatPrice(feePrice || 0, currency)} ${i18n.t(
          'pricing_plans.fixed_fee'
        )} ${yearlyText},`,
        i18n.t('self_upsell.first_step.expenses_seats_included', {
          seats: includedSeatsPerModule,
        }),
      ]
        .filter(Boolean)
        .join(' ')
    : [
        perSeatPrice
          ? `${formatPrice(perSeatPrice, currency)}/mo x ${i18n.t('self_upsell.common.seats', {
              seats: currentSeats,
            })} ${yearlyText}`
          : null,
        feePrice
          ? `${formatPrice(feePrice, currency)} ${i18n.t('pricing_plans.fixed_fee')} ${yearlyText}`
          : null,
        stairstepPrice
          ? `${formatPrice(stairstepPrice, currency)} ${i18n.t(
              'pricing_plans.fixed_fee'
            )} ${yearlyText}`
          : null,
      ]
        .filter(Boolean)
        .join(' ')

  // Get discount info for main addon
  const mainAddonPlanId = addon.pricing.getPlanId(period)
  const mainAddonDiscountInfo = discountedAddons.get(mainAddonPlanId || '')

  const getDiscountDescription = (discount: ApiDiscount): string => {
    if (discount.discountType === 'item_level_discount') {
      const periodValue = discount.period || manualDiscount?.period || DEFAULT_DISCOUNT_PERIOD.VALUE
      const periodUnit =
        (discount.periodUnit && getPeriodUnitTranslation(discount.periodUnit)) ||
        manualDiscount?.periodUnit ||
        i18n.t('self_upsell.second_step.discount_periods.month')
      return i18n.t('self_upsell.second_step.discount_for_period', {
        discount: discount.discountPercentage,
        period: periodValue,
        periodUnit,
      })
    }
    return i18n.t('self_upsell.second_step.discount_description')
  }

  // Helper: Check if an addon is in configPlan.addon
  // Installation status is already handled in the previous step - if installed, it won't be in configPlan
  const isAddonInConfigPlan = (addonPlan: Plan): boolean => {
    if (!configPlan.addon) return false
    const planId = addonPlan.pricing.getPlanId(period) || ''
    return configPlan.addon.includes(planId)
  }

  // Helper: Get addon item info (price, discount, description)
  const getAddonItemInfo = (addonPlan: Plan) => {
    const planId = addonPlan.pricing.getPlanId(period) || ''
    const discountInfo = discountedAddons.get(planId)
    const originalPrice = addonPlan.pricing.getTotalPrice(period, currentSeats)

    const baseDescription = `${formatPrice(
      addonPlan.pricing.getSeatsPrice(period) || 0,
      currency
    )}/mo x ${i18n.t('self_upsell.common.seats', {
      seats: currentSeats,
    })} ${yearlyText}`

    const description = discountInfo ? (
      <>
        <Text.Normal size="100" color="grey700">
          {getDiscountDescription(discountInfo.discount)}
        </Text.Normal>
        <Text.Normal size="100" color="grey700">
          {baseDescription}
        </Text.Normal>
      </>
    ) : (
      baseDescription
    )

    const price = discountInfo
      ? formatPrice(discountInfo.discountedPrice, currency)
      : formatPrice(originalPrice, currency)

    const originalPriceFormatted = discountInfo ? formatPrice(originalPrice, currency) : undefined

    return {
      title: addonPlan.title,
      description,
      price,
      originalPrice: originalPriceFormatted,
    }
  }

  // Helper: Render cross-selling addon items
  // Only renders addons that are in configPlan.addon (installation status already handled in previous step)
  const renderCrossSellingAddons = (addons: Plan[] | undefined) => {
    if (!addons) return null
    return addons
      .filter((crossSellingAddon) => {
        // Only show addons that are in configPlan.addon
        return isAddonInConfigPlan(crossSellingAddon)
      })
      .map((crossSellingAddon) => {
        const itemInfo = getAddonItemInfo(crossSellingAddon)
        return <ItemInfo key={crossSellingAddon.title} {...itemInfo} />
      })
  }
  const itemInfo = () => {
    const variant = addon.getSpecificVariant(configPlan.variant || 'Business')
    const priceSeats = isExpenses() ? seats : currentSeats
    const originalPrice = variant?.pricing.getTotalPrice(period, priceSeats) || 0

    const title = `${addon.title} ${variant?.type}`
    const price = mainAddonDiscountInfo
      ? formatPrice(mainAddonDiscountInfo.discountedPrice, currency)
      : formatPrice(originalPrice, currency)
    const originalPriceFormatted = mainAddonDiscountInfo
      ? formatPrice(originalPrice, currency)
      : undefined

    const description = mainAddonDiscountInfo ? (
      <>
        <Text.Normal size="100" color="grey700">
          {getDiscountDescription(mainAddonDiscountInfo.discount)}
        </Text.Normal>
        <Text.Normal size="100" color="grey700">
          {descriptionText}
        </Text.Normal>
      </>
    ) : (
      descriptionText
    )

    return (
      <ItemInfo
        key={addon.title}
        title={title}
        description={description}
        price={price}
        originalPrice={originalPriceFormatted}
      />
    )
  }

  if (!configPlan.variant) return null
  return (
    <Box
      border={{
        all: {
          width: 's1',
          style: 'solid',
          color: 'grey300',
        },
      }}
      borderRadius={{ all: 'abs016' }}
      background="grey100"
      padding="s8"
      gap="s8"
    >
      <Box
        borderRadius={{ all: 'abs012' }}
        background="grey000White"
        boxShadow="s50"
        style={{ overflowX: 'hidden' }}
      >
        <Box gap="s12" padding="s16">
          {tieredPrice?.selectedPrice && feePrice ? (
            <>
              <ItemInfo
                key={addon.title}
                title={addon.title}
                description={descriptionText}
                price={formatPrice(annualFeePrice || 0, currency)}
              />
              <ItemInfo
                key={addon.title}
                title={i18n.t('self_upsell.second_step.tiered_seats_title', {
                  addon: addon.title,
                })}
                description={i18n.t('self_upsell.second_step.tiered_seats', {
                  seats: seats - (tieredPrice.tiers?.[0]?.endingUnit || 0),
                })}
                price={formatPrice(tieredPrice.selectedPrice, currency)}
              />
            </>
          ) : (
            itemInfo()
          )}

          {renderCrossSellingAddons(mandatoryCrossSellingAddons)}
          {renderCrossSellingAddons(crossSellingAddons)}
        </Box>
        <Box
          border={{
            top: {
              width: 's1',
              style: 'dashed',
              color: 'grey200',
            },
          }}
          flexDirection="row"
          justifyContent="spaceBetween"
          gap="s8"
          padding="s16"
        >
          <Text.Normal size="200" weight="semibold" color="grey1000">
            {i18n.t('self_upsell.common.total')}
          </Text.Normal>
          <Text.Normal size="200" weight="semibold" color="grey1000">
            {loadingPrice ? <Spinner size="small" /> : formattedTotalPrice}
          </Text.Normal>
        </Box>
      </Box>

      <Box paddingX="s12" paddingY="s8">
        <Text.Normal size="200" color="grey800">
          {i18n.t('self_upsell.second_step.description_1')}{' '}
          <strong style={{ fontWeight: 500 }}>{formattedTotalPrice}</strong>{' '}
          {i18n.t('self_upsell.second_step.description_2')}{' '}
          <strong style={{ fontWeight: 500 }}>{billingDate}</strong>.{' '}
          {i18n.t('self_upsell.second_step.description_3')}
        </Text.Normal>
      </Box>
    </Box>
  )
}

export default PaymentDetail
